import { FC, Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useToggle } from "react-use";
import { Context } from "@/lib/Context";
import { useAtomValue } from "jotai";
import { isUndefined, round } from "lodash";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";

import { Stock } from "@/models/stock";
import Skeleton from "../skeleton/Skeleton";
import Module from "@/components/module/Module";
import { formatDate, nFormatter } from "@/lib/utils";
import { StockOptionsInTheMoney } from "@/models/stock-options";
import { getStockOptionsInTheMoney } from "@/controllers/stock-options";
import SmallObfuscationBanner from "../obfuscation/SmallObfuscationBanner";
import { agreementsState, backgroundColorState, blackLighterState, lighterBackgroundColorState, textColorState, themeState, whiteDarkerState } from "@/lib/store";

import styles from "./StockOptionsITM.module.scss";
import { itmFetchDelay } from "./itmFetchDelay";
import { brighten } from "@/lib/ortex-charts/color";

const expiryDaysOptions = [1, 7, 30, 90, 180, 365];

const PRICE_LEVELS = [
    { multiplier: 0.9, label: "-10%" },
    { multiplier: 0.93, label: "-7%" },
    { multiplier: 0.95, label: "-5%" },
    { multiplier: 0.96, label: "-4%" },
    { multiplier: 0.97, label: "-3%" },
    { multiplier: 0.98, label: "-2%" },
    { multiplier: 0.99, label: "-1%" },
    { multiplier: 1.0, label: "", isHighlighted: true },
    { multiplier: 1.01, label: "+1%" },
    { multiplier: 1.02, label: "+2%" },
    { multiplier: 1.03, label: "+3%" },
    { multiplier: 1.04, label: "+4%" },
    { multiplier: 1.05, label: "+5%" },
    { multiplier: 1.07, label: "+7%" },
    { multiplier: 1.1, label: "+10%" },
];

const StockOptionsITM: FC<{ stock: Stock }> = ({ stock }) => {
    const { ortexWebSocket } = useContext(Context);

    const theme = useAtomValue(themeState);
    const textColor = useAtomValue(textColorState);
    const whiteDarker = useAtomValue(whiteDarkerState);
    const blackLighter = useAtomValue(blackLighterState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const agreements = useAtomValue(agreementsState);

    const [itmValue, toggleITMValue] = useToggle(false);

    const [obfuscated, setObfuscated] = useState(false);
    const [stockPrice, setStockPrice] = useState<number>();
    const [inTheMoney, setInTheMoney] = useState({} as StockOptionsInTheMoney);
    const [itmExpiryDays, setITMExpiryDays] = useState(30);
    const [itmPrice, setITMPrice] = useState("");
    const [itmLoading, setITMLoading] = useState(true);

    const firstPrice = useRef<number>(undefined);
    const itmTableRef = useRef<HTMLDivElement>(null);

    const priceMessage = useCallback(
        ({ data }: MessageEvent) => {
            const parsedData = JSON.parse(data);
            if (parsedData.T) setStockPrice(parsedData.T.C.c);
            else if (parsedData.LT) {
                const history = parsedData.LT;
                setStockPrice(history[history.length - 1]?.[0].c);
            }
        },
        [ortexWebSocket]
    );

    useEffect(() => {
        if (ortexWebSocket) ortexWebSocket.addEventListener("message", priceMessage);

        return () => {
            if (ortexWebSocket) ortexWebSocket.removeEventListener("message", priceMessage);
        };
    }, [stock, ortexWebSocket]);

    useEffect(() => {
        if (!isUndefined(stockPrice) && isUndefined(firstPrice.current)) {
            firstPrice.current = stockPrice;
            setITMPrice(round(stockPrice, 2).toString());
        }
    }, [stockPrice]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        if (agreements?.find(({ code }) => code === "OPRA") && itmPrice) {
            setITMLoading(true);
            const load = () =>
                getStockOptionsInTheMoney(stock.tradingitem_id, itmPrice, itmExpiryDays)
                    .then((data) => {
                        setInTheMoney(data);
                        setObfuscated(data.obfuscated);

                        !data?.obfuscated && setTimeout(() => itmTableRef.current?.scroll(0, itmTableRef.current.scrollHeight / 2 - itmTableRef.current.clientHeight / 2 + 14), 0);
                    })
                    .finally(() => setITMLoading(false));
            // The price from the first tick loads at once; edits to the field are debounced.
            const delay = itmFetchDelay(itmPrice, firstPrice.current);
            if (delay === 0) load();
            else timeout = setTimeout(load, delay);
        }
        return () => {
            if (agreements?.find(({ code }) => code === "OPRA") && itmPrice) clearTimeout(timeout);
        };
    }, [agreements, itmPrice, itmExpiryDays]);

    const price = Number(itmPrice);
    const expiries = Object.entries(inTheMoney).filter(([key]) => dayjs(key).isValid());

    const getFieldKey = (type: "P" | "C", percentageLabel: string) => {
        const suffix = itmValue ? "Value" : "";
        const pcValue = percentageLabel.replace("%", "pc").replace("+", "");
        // Negative values need underscore prefix: "_-10pc", positive/zero don't: "0pc", "1pc"
        const prefix = pcValue.startsWith("-") ? "_" : "";
        return `${type}InMoney${suffix}${prefix}${pcValue}`;
    };

    const renderTableRow = (level: (typeof PRICE_LEVELS)[number]) => {
        const calculatedPrice = (price * level.multiplier).toFixed(2);
        const priceLabel = level.label ? `${calculatedPrice} (${level.label})` : calculatedPrice;
        const pcValue = level.label || "0pc";

        return (
            <tr key={level.label} className={level.isHighlighted ? styles.highlighted : undefined}>
                <td style={{ backgroundColor, borderColor: theme === "dark" ? blackLighter : whiteDarker }}>{priceLabel}</td>
                {expiries.map(([epoch, values]) => (
                    <Fragment key={epoch}>
                        <td className="negative">{nFormatter(values[getFieldKey("P", pcValue)] as number, 2)}</td>
                        <td className="positive">{nFormatter(values[getFieldKey("C", pcValue)] as number, 2)}</td>
                    </Fragment>
                ))}
                <td className="negative">{nFormatter(inTheMoney[getFieldKey("P", pcValue)] as number, 2)}</td>
                <td className="positive">{nFormatter(inTheMoney[getFieldKey("C", pcValue)] as number, 2)}</td>
            </tr>
        );
    };

    const renderFilters = (
        <>
            <Button size="small" variant="outlined" sx={{ color: textColor, fontSize: 12, height: 26, padding: "3px 5px", textTransform: "none", mb: 1 }} onClick={toggleITMValue}>
                View as {itmValue ? "Shares" : "Value"}
            </Button>
            <div className={styles["settings"]}>
                <TextField
                    size="small"
                    id="outlined-number"
                    label="Using Price"
                    type="number"
                    value={itmPrice}
                    onChange={obfuscated ? undefined : (e) => setITMPrice(e.target.value)}
                    style={{ color: textColor, marginTop: "10px" }}
                    slotProps={{
                        inputLabel: {
                            shrink: true,
                            style: { color: textColor },
                        },
                    }}
                />
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                        Consider options that expire the next
                    </Typography>
                    <FormControl>
                        <InputLabel id="expiry-days-label" style={{ color: textColor }}>
                            Days
                        </InputLabel>
                        <Select size="small" labelId="expiry-days-label" id="expiry-days-select" label="Days" value={itmExpiryDays} onChange={obfuscated ? undefined : (e) => setITMExpiryDays(Number(e.target.value))} style={{ color: textColor }}>
                            {expiryDaysOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </div>
        </>
    );

    return itmLoading ? (
        <Skeleton variant="rounded" width="100%" height="100%" loading={itmLoading} />
    ) : (
        <Module title="In The Money" help={{ moduleId: "in_the_money", modulePage: "V2_stock_options" }} allowFullscreen filters={renderFilters}>
            <div className={styles.StockOptionsITM}>
                <div className={styles["table"]} ref={itmTableRef} style={{ overflow: obfuscated ? "hidden" : "auto" }}>
                    <style jsx>{`
                        th,
                        td:first-child {
                            background-color: ${backgroundColor};
                        }
                        table,
                        th,
                        td {
                            border-color: ${theme === "dark" ? blackLighter : whiteDarker};
                        }
                        td:not(:first-child):before {
                            content: "${itmValue ? "$ " : ""}";
                        }
                        .${styles.highlighted} {
                            background-color: ${brighten(backgroundColor, 0.07)};
                        }
                        @media (hover: hover) {
                            button:hover {
                                background-color: ${lighterBackgroundColor} !important;
                            }
                        }
                    `}</style>
                    <table>
                        <thead>
                            <tr>
                                <th style={{ borderBottomWidth: 2 }}>Stock Price</th>
                                {expiries.map(([epoch]) => (
                                    <th key={epoch} colSpan={2}>
                                        {formatDate(dayjs.utc(Number(epoch)))}
                                    </th>
                                ))}
                                <th colSpan={2}>All EXP</th>
                            </tr>
                            <tr>
                                <th style={{ borderTopWidth: 0 }}>-</th>
                                {expiries.map(([epoch]) => (
                                    <Fragment key={epoch}>
                                        <th className="negative">Puts</th>
                                        <th className="positive">Calls</th>
                                    </Fragment>
                                ))}
                                <th className="negative">Puts</th>
                                <th className="positive">Calls</th>
                            </tr>
                        </thead>
                        {obfuscated ? <SmallObfuscationBanner /> : null}
                        <tbody className={obfuscated ? "obfuscated" : undefined}>{PRICE_LEVELS.map(renderTableRow)}</tbody>
                    </table>
                </div>
            </div>
        </Module>
    );
};

export default StockOptionsITM;
